import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const KINDS = [["tithes", "Tithe"], ["donations", "Conference Collection"], ["other", "Other Collection"], ["registrations", "Paid Registrations"]];

export function BackfillReceipts({ open, dest, onClose, onDone }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [kinds, setKinds] = useState(KINDS.map(([k]) => k));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setPreview(null); setBusy(false); };
  const close = () => { reset(); onClose(); };
  const call = async (create) => {
    setBusy(true);
    try {
      const r = (await api.post("/receipts/backfill", { destination: dest, start, end, kinds, create })).data;
      if (!create) { setPreview(r); setBusy(false); return; }
      toast.success(r.created ? `${r.created} receipt(s) created (${r.first} – ${r.last})` : "No new receipts were needed");
      onDone(); close();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const toggle = (k, v) => { setPreview(null); setKinds((ks) => (v ? [...ks, k] : ks.filter((x) => x !== k))); };
  const ok = start && end && kinds.length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg" data-testid="backfill-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Create receipts from old entries · {dest}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Makes one receipt for each {dest} entry in this date range that has no receipt yet. Nothing is emailed.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-semibold">From<Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPreview(null); }} className="mt-1 h-10 bg-white" data-testid="backfill-start" /></label>
          <label className="text-sm font-semibold">To<Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPreview(null); }} className="mt-1 h-10 bg-white" data-testid="backfill-end" /></label>
        </div>
        <div className="space-y-2" data-testid="backfill-kinds">
          {KINDS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={kinds.includes(k)} onCheckedChange={(v) => toggle(k, !!v)} data-testid={`backfill-kind-${k}`} /> {label}</label>
          ))}
        </div>
        {preview && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="backfill-preview">
            <p className="font-semibold" data-testid="backfill-count">{preview.count ? `This will create ${preview.count} receipt(s).` : "All entries in this range already have receipts."}</p>
            <p>{KINDS.filter(([k]) => k in preview.by_kind).map(([k, l]) => `${l}: ${preview.by_kind[k]}`).join(" · ")}</p>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={close} data-testid="backfill-cancel">Cancel</Button>
          {preview && preview.count > 0
            ? <Button onClick={() => call(true)} disabled={busy} data-testid="backfill-create-btn">{busy ? "Creating…" : `Create ${preview.count} receipt(s)`}</Button>
            : <Button onClick={() => call(false)} disabled={!ok || busy} data-testid="backfill-check-btn">{busy ? "Checking…" : "Check how many"}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
